import { useOutletContext, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del, put } from "@/lib/api";
import { Section } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { money } from "@/lib/format";
import { Check, Plus, Pencil, Trash2, ExternalLink, GitBranch, Rocket, Globe, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const APPROVAL_TYPES = ["Module", "Task"];

export default function ProjectOverview() {
  const { project } = useOutletContext();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useApp();
  const [editInfo, setEditInfo] = useState(false);
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const { data: roles = [] } = useQuery({ queryKey: ["roles"], queryFn: () => get("/roles") });
  const { data: approvalConfig } = useQuery({ queryKey: ["approval-config", project.id], queryFn: () => get(`/pm/projects/${project.id}/approval-config`) });
  const [acForm, setAcForm] = useState(null);
  const ac = acForm || approvalConfig || { applies_to_types: [], l1_approver_role: "", l2_approver_role: "" };

  const inval = () => qc.invalidateQueries({ queryKey: ["project", project.id] });
  const addMember = useMutation({ mutationFn: (b) => post(`/pm/projects/${project.id}/members`, b), onSuccess: () => { inval(); toast.success("Member added"); } });
  const removeMember = useMutation({ mutationFn: (uid) => del(`/pm/projects/${project.id}/members/${uid}`), onSuccess: () => { inval(); toast.success("Member removed"); } });
  const updateInfo = useMutation({ mutationFn: (b) => patch(`/pm/projects/${project.id}`, b), onSuccess: () => { inval(); toast.success("Project info updated"); setEditInfo(false); } });
  const saveApprovalConfig = useMutation({
    mutationFn: (b) => put(`/pm/projects/${project.id}/approval-config`, b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["approval-config", project.id] }); toast.success("Approval workflow saved"); setAcForm(null); },
  });

  const stats = project.item_stats || { total: 0, done: 0 };
  const closure = [
    { label: "All modules delivered", value: `${stats.done}/${stats.total} items done`, done: stats.total > 0 && stats.done === stats.total },
    { label: "Data migration complete", value: null, done: project.progress > 50 },
    { label: "Testing complete", value: null, done: project.progress > 70 },
    { label: "Client final sign-off", value: null, done: false },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
      <div className="space-y-6">
        <Section title="Description"><div className="p-4 text-sm text-foreground">{project.description}</div></Section>
        <Section title="Closure Checklist">
          <div className="divide-y divide-border">
            {closure.map((c) => (
              <div key={c.label} className="flex items-center gap-3 px-4 py-3">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full ${c.done ? "bg-emerald-500 text-white" : "border-2 border-border"}`}>
                  {c.done && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1 text-sm">{c.label}</span>
                {c.value && <span className="text-xs text-muted-foreground">{c.value}</span>}
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-sm font-semibold">Project</h3>
          <dl className="mt-3 space-y-2 text-sm">
            {can("crm", "view") && (
              <div className="flex justify-between"><dt className="text-muted-foreground">Client</dt><dd>{project.client_name || "Internal"}</dd></div>
            )}
            <div className="flex justify-between"><dt className="text-muted-foreground">Budget</dt><dd className="font-medium">{money(project.budget)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Progress</dt><dd className="font-medium">{project.progress}%</dd></div>
            <div className="flex justify-between items-center"><dt className="text-muted-foreground">Health</dt><dd><StatusBadge family={project.health} label={project.health} /></dd></div>
            {project.logged_hours != null && <div className="flex justify-between"><dt className="text-muted-foreground">Logged Hours</dt><dd className="font-medium">{project.logged_hours}h</dd></div>}
          </dl>
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="project-info-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold">Project Info</h3>
            <button data-testid="edit-project-info-btn" onClick={() => setEditInfo(true)} className="text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
          </div>
          <div className="mt-3 space-y-2 text-sm">
            <InfoLink icon={GitBranch} label="Repo" url={project.repo_url} />
            <InfoLink icon={Rocket} label="Deployed" url={project.deployed_link} />
            <InfoLink icon={Globe} label="Production" url={project.production_link} />
            {(project.docs_links || []).map((d, i) => (
              <InfoLink key={i} icon={FileText} label={d.label || "Doc"} url={d.url} />
            ))}
            {!project.repo_url && !project.deployed_link && !project.production_link && !(project.docs_links || []).length && (
              <p className="text-xs text-muted-foreground">No links added yet.</p>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold">Team</h3>
            <FormDialog title="Add Member" testid="add-member-modal"
              trigger={<button data-testid="add-member-btn" className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus className="h-3.5 w-3.5" />Add</button>}
              fields={[
                { name: "user_id", label: "Employee", type: "select", required: true, full: true, options: users.map((u) => ({ value: u.id, label: u.name })) },
                { name: "role", label: "Project Role", full: true, placeholder: "e.g. Engineer" },
                { name: "allocation_pct", label: "Allocation %", type: "number", placeholder: "e.g. 50" },
              ]}
              onSubmit={(v) => addMember.mutateAsync(v)} />
          </div>
          <div className="mt-3 space-y-2">
            {project.members?.map((m) => (
              <div key={m.user_id} className="group flex items-center gap-2">
                <Avatar user={umap[m.user_id]} size="sm" />
                <div className="flex-1"><div className="text-[13px] font-medium">{umap[m.user_id]?.name}</div><div className="text-xs text-muted-foreground">{m.role}{m.allocation_pct != null ? ` · ${m.allocation_pct}%` : ""}</div></div>
                <ConfirmDelete testid={`del-member-${m.user_id}`} title={`Remove ${umap[m.user_id]?.name} from this project?`}
                  trigger={<button className="hidden rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600 group-hover:block"><Trash2 className="h-3.5 w-3.5" /></button>}
                  onConfirm={() => removeMember.mutate(m.user_id)} />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="approval-workflow-card">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-display text-sm font-semibold">Approval Workflow</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {approvalConfig ? "Modules/Tasks require sign-off before reaching Done." : "No approval gate configured — DoD alone moves items to Done."}
          </p>
          <div className="mt-3 space-y-2">
            <div>
              <Label className="text-xs">Applies to</Label>
              <div className="mt-1 flex gap-3">
                {APPROVAL_TYPES.map((t) => (
                  <label key={t} className="flex items-center gap-1.5 text-xs">
                    <Checkbox data-testid={`ac-type-${t}`} disabled={!can("pm", "edit")}
                      checked={ac.applies_to_types.includes(t)}
                      onCheckedChange={(v) => setAcForm({ ...ac, applies_to_types: v ? [...ac.applies_to_types, t] : ac.applies_to_types.filter((x) => x !== t) })} />
                    {t}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">L1 Approver Role</Label>
              <Select disabled={!can("pm", "edit")} value={ac.l1_approver_role || ""} onValueChange={(v) => setAcForm({ ...ac, l1_approver_role: v })}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="ac-l1-role"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">L2 Approver Role</Label>
              <Select disabled={!can("pm", "edit")} value={ac.l2_approver_role || ""} onValueChange={(v) => setAcForm({ ...ac, l2_approver_role: v })}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="ac-l2-role"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {can("pm", "edit") && acForm && (
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="ghost" onClick={() => setAcForm(null)}>Cancel</Button>
                <Button size="sm" data-testid="ac-save" onClick={() => saveApprovalConfig.mutate(ac)}>Save</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ProjectInfoModal open={editInfo} setOpen={setEditInfo} project={project} onSubmit={(v) => updateInfo.mutate(v)} />
    </div>
  );
}

function InfoLink({ icon: Icon, label, url }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
    </a>
  );
}

function ProjectInfoModal({ open, setOpen, project, onSubmit }) {
  const [form, setForm] = useState(null);
  const f = form || { repo_url: project.repo_url || "", deployed_link: project.deployed_link || "", production_link: project.production_link || "", docs_links: project.docs_links || [] };
  const set = (k, v) => setForm({ ...f, [k]: v });
  const setDoc = (i, k, v) => set("docs_links", f.docs_links.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const addDoc = () => set("docs_links", [...f.docs_links, { label: "", url: "" }]);
  const removeDoc = (i) => set("docs_links", f.docs_links.filter((_, idx) => idx !== i));

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm(null); }}>
      <DialogContent className="max-w-md" data-testid="project-info-modal">
        <DialogHeader><DialogTitle>Edit Project Info</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Repo URL</Label><Input data-testid="pi-repo" className="mt-1" value={f.repo_url} onChange={(e) => set("repo_url", e.target.value)} /></div>
          <div><Label>Deployed Link</Label><Input data-testid="pi-deployed" className="mt-1" value={f.deployed_link} onChange={(e) => set("deployed_link", e.target.value)} /></div>
          <div><Label>Production Link</Label><Input data-testid="pi-prod" className="mt-1" value={f.production_link} onChange={(e) => set("production_link", e.target.value)} /></div>
          <div>
            <div className="flex items-center justify-between"><Label>Docs / Guidelines</Label>
              <button type="button" onClick={addDoc} className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus className="h-3.5 w-3.5" />Add</button>
            </div>
            <div className="mt-2 space-y-2">
              {f.docs_links.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Label" className="w-28" value={d.label} onChange={(e) => setDoc(i, "label", e.target.value)} />
                  <Input placeholder="URL" className="flex-1" value={d.url} onChange={(e) => setDoc(i, "url", e.target.value)} />
                  <button type="button" onClick={() => removeDoc(i)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="pi-submit" onClick={() => onSubmit(f)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
